import { IFactoryProductElement } from './factory-element.model';
import {
  IElementSelectProperty,
  IElementSwitchProperty,
  IFactoryProperty,
} from './element-property.model';
import {
  ElementQuantityExpressionName,
  FactoryQuantityRecord,
  IElementQuantityProperty,
  IFactoryQuantityProperty,
} from './element-quantities.model';
import { IElement, IProductElement, Project } from './project.model';
import { ArrayOrSingle, OptionalReadonly } from './type-helpers.model';
import { Recipe } from './recipe.model';
import { Results } from './unit.model';
import { ElementPath } from '../helpers/recursive-element.helpers';
import { ExpressionVariables } from '../helpers/expression-variables.helpers';
import { ProductID } from './product.model';
import { Orientation } from './orientation.model';
import { IConditionalNode } from '../helpers/conditional-nodes.helpers';
import { IOrganizationWhitelistItem } from './organization.model';

/**
 * Unique ids for element categories.
 * Note: new ids might be added, NEVER CHANGED!
 */
export enum ElementCategoryID {
  /**
   * Empty, not selected
   */
  None = '',

  // Product categories
  Concrete = '22',
  Wood = '46',
  Insulation = '47',
  Metal = '48',
  Gypsum = '49',
  Ceramics = '50',
  WindowsDoors = '53',
  Labour = '60',
  Energy = '61',
  AirHandlingUnit = '62',
  Heating = '63',
  Cooling = '65',
  OtherProduct = 'other-product',

  // System categories
  Column = '13',
  Beam = '12',
  Wall = '51',
  Slab = '52',
  Maintenance = '64',
  Other = 'other',
}

export const systemCategoryIds = [
  ElementCategoryID.Column,
  ElementCategoryID.Beam,
  ElementCategoryID.Wall,
  ElementCategoryID.Slab,
  ElementCategoryID.Other,
] as const;

export const systemCategoryIdsSet = new Set<string>(systemCategoryIds);

export type SystemCategoryId = (typeof systemCategoryIds)[number];

export const productCategoryIds = [
  ElementCategoryID.Concrete,
  ElementCategoryID.Wood,
  ElementCategoryID.Insulation,
  ElementCategoryID.Metal,
  ElementCategoryID.Gypsum,
  ElementCategoryID.Ceramics,
  ElementCategoryID.WindowsDoors,
  ElementCategoryID.Labour,
  ElementCategoryID.OtherProduct,
  ElementCategoryID.Energy,
] as const;

export type ProductCategoryId = (typeof productCategoryIds)[number];

const productCategoryIdsSet = new Set<string>(productCategoryIds);
export const isProductCategoryId = (id: string): id is ProductCategoryId =>
  productCategoryIdsSet.has(id);

export const serviceCategoryIds = [
  ElementCategoryID.Heating,
  ElementCategoryID.Maintenance,
  ElementCategoryID.AirHandlingUnit,
  ElementCategoryID.Cooling,
] as const;

export type ServiceCategoryId = (typeof serviceCategoryIds)[number];
export const serviceCategoryIdsSet = new Set<string>(serviceCategoryIds);

export const autoGeneratedChildrenCategoryIds = [
  ...productCategoryIds,
  ElementCategoryID.Heating,
  ElementCategoryID.AirHandlingUnit,
  ElementCategoryID.Cooling,
] as const;

/**
 * Categories that generates children / product elements
 */
export type AutoGeneratedChildrenCategoryId =
  (typeof autoGeneratedChildrenCategoryIds)[number];
export const autoGeneratedChildrenCategoryIdsSet = new Set<string>(
  autoGeneratedChildrenCategoryIds,
);

export type IElementCategoryQuantityPropertiesFn = (
  element?: IElement,
) => FactoryQuantityRecord | IFactoryQuantityProperty[];

export type IElementCategoryElementPropertiesFn = (
  element: IElement,
) => IConditionalNode<OptionalReadonly<IFactoryProperty>>[];

/**
 * Resolves the category's templated children for an element.
 * Return `undefined` to signal "unable to resolve children right now — leave
 * existing children untouched" (used for transient invalid states where a
 * Select count temporarily falls outside its conditional map). Returning an
 * empty array still means "this category produces no children", which causes
 * downstream cleanup to strip previously generated children.
 */
export type IElementCategoryElementsFn = (
  element: IElement,
) =>
  | ArrayOrSingle<IConditionalNode<IFactoryProductElement | ProductID>>
  | undefined;

export type IElementCategoryPropertySelectionByProductIdFn = (
  productId: ProductID,
) =>
  | {
      [key: string]:
        IElementSelectProperty['count'] | IElementSwitchProperty['count'];
    }
  | undefined;

export interface PropertiesOptions {
  [key: string]: {
    property: IFactoryProperty;
    propertyName: string;
    propertyType?: string;
  };
}

export interface IElementCategoryMapResultsContext {
  path: ElementPath;
  project: Project;
  productElement: IProductElement;
  categoryElement: IElement;
  /** Gross floor area (BTA) of the project, resolved once per rollup (DEV-3321) */
  gfa: number;
}

export interface IElementCategory extends IOrganizationWhitelistItem {
  id: ElementCategoryID;
  parent_id?: ElementCategoryID;
  name: string;

  /**
   * List of properties to add to elements using this category (optional)
   */
  properties?: OptionalReadonly<IConditionalNode<IFactoryProperty>[]>;

  /**
   * List of predefined properties that can be added by the user
   */
  optionalProperties?: OptionalReadonly<IConditionalNode<IFactoryProperty>[]>;

  /**
   * Pass true to hide category from the list
   */
  disabled?: boolean;

  /**
   * (Optional) Which quantity should be selected by default.
   */
  defaultSelectedQuantity?: ElementQuantityExpressionName;

  /**
   * Pass true to disable quantity selection for the category
   */
  disableQuantitySelection?: boolean;

  /**
   * Display color for the advanced product that belongs to this category
   */
  color?: string;

  /**
   * Optional orientation for the category to help to map which dimension is most relevant for the category
   */
  orientation?: Orientation;

  /**
   * Make the name of the category element in cursive in element list
   */
  cursiveName?: boolean;

  /**
   * Optional function to make category control chilren of the element
   */
  getChildElements?: IElementCategoryElementsFn;

  /**
   * Optional function to make category control quantity properties
   */
  getQuantityProperties?: IElementCategoryQuantityPropertiesFn;

  /**
   * Tell werther the recipe should be listed in the category or not
   * @param recipe
   * @param element
   */
  recipeFilter?: (recipe: Recipe, element: IElement) => boolean;

  /**
   * Optional function to modify the expression variables AFTER resolving the element properties of an element
   */
  mapExpressionVariablesAfter?: (
    variables: ExpressionVariables,
    element: IElement,
  ) => ExpressionVariables;

  /**
   * Optional function to modify the expression variables BEFORE resolving the element properties of an element
   */
  mapExpressionVariablesBefore?: (
    variables: ExpressionVariables,
    element: IElement,
  ) => ExpressionVariables;

  /**
   * Optional function to modify the results calculation
   */
  mapResults?: (
    results: Results,
    context: IElementCategoryMapResultsContext,
  ) => Results;

  /**
   * Get the selection required to get a certain product from the product tree.
   */
  getElementPropertySelectionByProductId: IElementCategoryPropertySelectionByProductIdFn;

  /**
   * Get a dynamic label for a quantity property.
   * Used for Energy to get "Annual energy" instead of "Energy"
   * If undefined is returned, the default label will be used
   */
  getQuantityPropertyLabel?: (
    property: IElementQuantityProperty,
    element: IElement,
  ) => string | undefined;
}

export type ElementCategoryConversionFactorRecord = Partial<
  Record<ElementCategoryID, Results>
>;

export type MainElementCategoryConversionFactorRecord = Partial<
  Record<ElementCategoryID, ElementCategoryConversionFactorRecord>
>;
